import React from 'react';
import { NavigateFunction } from 'react-router-dom';

import { Button, Chip, Dropdown, DropdownOption, Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { PopoverFilterEntry } from '~/components';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { useNavigate } from '~/lib/navigation';
import { TransferAmount } from '~/lib/transfers';
import { LoanRepository } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';

const getPaymentStatusChip = (status: string | undefined) => {
  if (!status) return null;

  const statusLower = status.toLowerCase();

  if (statusLower === 'completed') {
    return (
      <Chip icon={<Icon.CircleCheck />} variant="subtle" type="success">
        Completed
      </Chip>
    );
  }
  if (statusLower === 'returned') {
    return (
      <Chip icon={<Icon.CircleCross />} variant="subtle" type="danger">
        Returned
      </Chip>
    );
  }

  return (
    <Chip variant="subtle" type="default">
      {status}
    </Chip>
  );
};

export const handleFetchPayments = async (params: any, navigate: NavigateFunction, addLoanLink?: boolean) => {
  return LoanRepository.getAllPayments(params).then((response) => {
    const entries: any = [];
    if (response.payments.length) {
      response.payments.map((entry: any) => {
        const options: DropdownOption[] = [];

        if (addLoanLink) {
          options.push({
            label: 'Loan',
            onClick: () => navigate(`${ROUTE.LOANS}/edit/${entry.loanId}`),
          });
        }

        entries.push({
          id: entry.id,
          description: [entry?.principalAmount ?? 0, entry.description, entry.currency],
          status: entry.status,
          created: entry.createdAt,
          action: (
            <Dropdown
              isInside
              positionRight
              options={[
                ...options,
                {
                  label: 'Edit',
                  onClick: () => navigate(`${ROUTE.LOANS}/payments/edit/${entry.id}`),
                },
              ]}
              variant="dots"
            />
          ),
        });
      });
    }
    return { entries, hasMore: Boolean(response?.hasMore) };
  });
};

export const getPaymentColumns = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<Record<string, any>>[] => [
  {
    header: 'Amount / Description',
    accessor: 'description',
    width: 'minmax(300px, 1fr)',
    className: 'min-w-0',
    cell: ({ value }) => (
      <TransferAmount amount={Number(value[0] ?? 0)} currencyCode={value[2]} description={value[1] ?? undefined} />
    ),
  },
  {
    header: 'Status',
    width: 'minmax(min-content, 160px)',
    cell: ({ value }) => getPaymentStatusChip(value),
    accessor: 'status',
  },
  getTimestampColumn<Record<string, any>>({ accessor: 'created', timeFormat, onTimeFormatChange, width: '160px' }),
  {
    header: '',
    width: 'min-content',
    accessor: 'action',
    cell: ({ value }) => <div onClick={(event) => event.stopPropagation()}>{value}</div>,
  },
];

export const paymentFilter: PopoverFilterEntry[] = [
  {
    id: 'created',
    label: 'Created',
    type: 'date',
  },
];

export const PageLoansPaymentOverview: React.FC = () => {
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('payments');

  return (
    <>
      <PageActions>
        <Button onClick={() => navigate(ROUTE.LOANS)} icon={<Icon.HandCoins />} size="small" variant="secondary">
          Loans
        </Button>
        <Button onClick={() => navigate(`${ROUTE.LOANS}/payments/create`)} icon={<Icon.Plus />} size="small">
          Create Payment
        </Button>
      </PageActions>

      <PaginatedDataTable<Record<string, any>>
        tableId="payments"
        fetch={(params) => handleFetchPayments(params, navigate, true)}
        filter={paymentFilter}
        empty={{
          icon: <Icon.AnimationHandCoins />,
          title: 'No payments found',
          action: (
            <Button onClick={() => navigate(`${ROUTE.LOANS}/payments/create`)} size="small" icon={<Icon.Plus />}>
              Create Payment
            </Button>
          ),
        }}
        columns={getPaymentColumns(timeFormat, onTimeFormatChange)}
        onRowClick={(row) => {
          navigate(`${ROUTE.LOANS}/payments/edit/${row.id}`);
        }}
      />
    </>
  );
};
